import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQueryWithReauth } from './baseQuery'
import type {
    LoginRequest,
    LoginResponse,
    LoginVerifyRequest,
    LoginVerifyResponse,
    RegisterRequest,
    RegisterResponse,
    User,
    SendOtpRequest,
    SendOtpResponse,
    VerifyOtpRequest,
    VerifyOtpResponse,
    CreateInvestigationRequest,
    CreateInvestigationResponse,
    Investigation,
    InvestigationStats,
    SubmitReportRequest,
    InvestigationDocument,
    DocumentValidationResponse,
    InitiateFlutterwavePaymentRequest,
    InitiateFlutterwavePaymentResponse,
    VerifyFlutterwavePaymentRequest,
    VerifyFlutterwavePaymentResponse,
    Transaction,
    ReusablePayment,
    Earning,
    EarningsSummary,
    EarningsBreakdown,
    PlatformEarningsStats,
    MarkEarningPaidResponse,
    BulkMarkPaidRequest,
    BulkMarkPaidResponse,
    DashboardStats,
    InvestigationTrend,
    RevenueTrend,
    TopAgent,
    StatusDistribution,
    FinancialStats,
    InviteAgentRequest,
    UserWithStats,
    UpdateUserStatusRequest,
    ReassignInvestigationRequest,
    PlatformStatistics,
    WaitlistEntry,
    WaitlistResponse,
    WaitlistStats,
    CheckEmailResponse,
    UpdateWaitlistRequest,
    BulkUpdateWaitlistRequest,
    DocumentType,
    EarningStatus,
    CheckOption,
    ScoringCriteriaResponse,
    RiskAssessmentSubmission,
    RiskAssessmentResponse,
    DocumentApprovalRequest,
    SupportTicket,
    CreateTicketRequest,
    UpdateTicketStatusRequest,
    UpdateTicketPriorityRequest,
    AssignTicketRequest,
    TicketStatistics,
    TicketStatus,
    TicketPriority,
    TicketCategory,
    BankAccount,
    AddBankAccountRequest,
    UpdateBankAccountRequest,
    Withdrawal,
    CreateWithdrawalRequest,
    ReviewWithdrawalRequest,
    ProcessWithdrawalRequest,
    WithdrawalStatistics,
    WithdrawalStatus,
} from '../../types'

export const api = createApi({
    reducerPath: 'api',
    baseQuery: baseQueryWithReauth,
    tagTypes: [
        'Auth',
        'User',
        'Investigation',
        'Transaction',
        'Earning',
        'Analytics',
        'Admin',
        'SupportTicket',
        'BankAccount',
        'Withdrawal',
        'Waitlist',
        'RiskAssessment',
        'CheckOptions',
    ],
    endpoints: (builder) => ({
        // ==================== AUTHENTICATION ====================
        login: builder.mutation<LoginResponse, LoginRequest>({
            query: (credentials) => ({
                url: '/auth/login',
                method: 'POST',
                body: credentials,
            }),
            invalidatesTags: ['Auth'],
        }),

        loginVerify: builder.mutation<LoginVerifyResponse, LoginVerifyRequest>({
            query: (data) => ({
                url: '/auth/login/verify',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Auth'],
        }),

        register: builder.mutation<RegisterResponse, RegisterRequest>({
            query: (userData) => ({
                url: '/auth/register',
                method: 'POST',
                body: userData,
            }),
        }),

        getProfile: builder.query<User, void>({
            query: () => '/auth/profile',
            transformResponse: (response: any) => response.data || response,
            providesTags: ['Auth'],
        }),

        sendOtp: builder.mutation<SendOtpResponse, SendOtpRequest>({
            query: (data) => ({
                url: '/auth/send-otp',
                method: 'POST',
                body: data,
            }),
        }),

        verifyOtp: builder.mutation<VerifyOtpResponse, VerifyOtpRequest>({
            query: (data) => ({
                url: '/auth/verify-otp',
                method: 'POST',
                body: data,
            }),
        }),

        sendEmailVerification: builder.mutation<{ success: boolean; message: string }, void>({
            query: () => ({
                url: '/auth/send-email-verification',
                method: 'POST',
            }),
        }),

        verifyEmail: builder.mutation<{ success: boolean; message: string }, { code: string }>({
            query: (data) => ({
                url: '/auth/verify-email',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Auth'],
        }),

        // ==================== INVESTIGATIONS ====================
        validateInvestigation: builder.mutation<{ valid: boolean; message: string; amount: number; checkType: string }, CreateInvestigationRequest>({
            query: (data) => ({
                url: '/investigations/validate',
                method: 'POST',
                body: data,
            }),
        }),

        createInvestigation: builder.mutation<CreateInvestigationResponse, { data: CreateInvestigationRequest; documents?: File[]; documentTypes?: string[] }>({
            query: ({ data, documents, documentTypes }) => {
                // If documents are provided, use FormData
                if (documents && documents.length > 0) {
                    const formData = new FormData();
                    
                    // Append all investigation data fields
                    formData.append('checkType', data.checkType);
                    formData.append('employeeFirstName', data.employeeFirstName);
                    if (data.employeeMiddleName) formData.append('employeeMiddleName', data.employeeMiddleName);
                    formData.append('employeeLastName', data.employeeLastName);
                    formData.append('employeeEmail', data.employeeEmail);
                    formData.append('employeePhone', data.employeePhone);
                    if (data.employeeGender) formData.append('employeeGender', data.employeeGender);
                    if (data.transactionId) formData.append('transactionId', data.transactionId);
                    
                    // Append documents
                    documents.forEach((file) => {
                        formData.append('documents', file);
                    });
                    
                    // Append document types if provided
                    if (documentTypes) {
                        formData.append('documentTypes', documentTypes.join(','));
                    }
                    
                    return {
                        url: '/investigations',
                        method: 'POST',
                        body: formData,
                    };
                }
                
                // Otherwise use JSON
                return {
                    url: '/investigations',
                    method: 'POST',
                    body: data,
                };
            },
            invalidatesTags: ['Investigation', 'Analytics'],
        }),

        getMyInvestigations: builder.query<Investigation[], { isPaid?: boolean } | void>({
            query: (args) => {
                const params: Record<string, string> = {};
                // Backend only accepts the literal strings "true"/"false". Skip the param
                // entirely when undefined so we get the full list.
                if (args && typeof args.isPaid === 'boolean') {
                    params.isPaid = args.isPaid ? 'true' : 'false';
                }
                return {
                    url: '/investigations/my-investigations',
                    params,
                };
            },
            transformResponse: (response: any) => response.data || response,
            providesTags: ['Investigation'],
        }),

        getInvestigationById: builder.query<Investigation, string>({
            query: (id) => `/investigations/${id}`,
            providesTags: (_result, _error, id) => [{ type: 'Investigation', id }],
        }),

        getInvestigationByLink: builder.query<Investigation, string>({
            query: (linkToken) => `/investigations/by-link/${linkToken}`,
        }),

        submitEmployeeForm: builder.mutation<Investigation, { linkToken: string; data: any }>({
            query: ({ linkToken, data }) => ({
                url: `/investigations/submit-form/${linkToken}`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Investigation'],
        }),

        getInvestigationStats: builder.query<InvestigationStats, void>({
            query: () => '/investigations/stats',
            providesTags: ['Analytics'],
        }),

        assignAgent: builder.mutation<Investigation, { id: string; agentId: string }>({
            query: ({ id, agentId }) => ({
                url: `/admin/investigations/${id}/assign`,
                method: 'PATCH',
                body: { agentId },
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Investigation', id }, 'Admin', 'Investigation'],
        }),

        startInvestigation: builder.mutation<Investigation, string>({
            query: (id) => ({
                url: `/investigations/${id}/start`,
                method: 'POST',
            }),
            invalidatesTags: (_result, _error, id) => [{ type: 'Investigation', id }, 'Investigation'],
        }),

        submitReport: builder.mutation<Investigation, { id: string; data: SubmitReportRequest }>({
            query: ({ id, data }) => ({
                url: `/investigations/${id}/submit-report`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [
                { type: 'Investigation', id },
                'Investigation',
                'Analytics',
            ],
        }),

        validateInvestigationReport: builder.mutation<Investigation, { id: string; approved: boolean; validationNotes?: string }>({
            query: ({ id, approved, validationNotes }) => ({
                url: `/investigations/${id}/validate-report`,
                method: 'POST',
                body: { approved, validationNotes },
            }),
            invalidatesTags: (_result, _error, { id }) => [
                { type: 'Investigation', id },
                'Investigation',
                'Earning',
                'Analytics',
            ],
        }),

        uploadInvestigationDocument: builder.mutation<
            InvestigationDocument,
            { id: string; file: File; documentType: DocumentType }
        >({
            query: ({ id, file, documentType }) => {
                const formData = new FormData()
                formData.append('file', file)
                formData.append('documentType', documentType)
                return {
                    url: `/investigations/${id}/documents/upload`,
                    method: 'POST',
                    body: formData,
                }
            },
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Investigation', id }],
        }),

        getInvestigationDocuments: builder.query<InvestigationDocument[], string>({
            query: (id) => `/investigations/${id}/documents`,
            providesTags: (_result, _error, id) => [{ type: 'Investigation', id }],
        }),

        validateRequiredDocuments: builder.query<DocumentValidationResponse, string>({
            query: (id) => `/investigations/${id}/documents/validate`,
            providesTags: (_result, _error, id) => [{ type: 'Investigation', id }],
        }),

        deleteDocument: builder.mutation<{ success: boolean; message: string }, { investigationId: string; documentId: string }>({
            query: ({ investigationId, documentId }) => ({
                url: `/investigations/${investigationId}/documents/${documentId}/delete`,
                method: 'POST',
            }),
            invalidatesTags: (_result, _error, { investigationId }) => [{ type: 'Investigation', id: investigationId }],
        }),

        getScoringCriteria: builder.query<ScoringCriteriaResponse, void>({
            query: () => '/investigations/scoring-criteria',
        }),

        submitRiskAssessment: builder.mutation<RiskAssessmentResponse, { id: string; data: RiskAssessmentSubmission }>({
            query: ({ id, data }) => ({
                url: `/investigations/${id}/risk-assessment`,
                method: 'POST',
                body: data,
            }),
            transformResponse: (response: any) => {
                if (response?.data && response?.success !== undefined) {
                    return response.data;
                }
                return response;
            },
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Investigation', id }, 'RiskAssessment'],
        }),

        getRiskAssessment: builder.query<RiskAssessmentResponse | null, string>({
            query: (id) => `/investigations/${id}/risk-assessment`,
            transformResponse: (response: any) => {
                // baseQuery unwraps outer { success, data } → inner may still be { success, data: {...} }
                if (response?.data && response?.success !== undefined) {
                    return response.data;
                }
                return response ?? null;
            },
            providesTags: (_result, _error, id) => [{ type: 'RiskAssessment', id }],
        }),

        getRiskAssessmentHistory: builder.query<RiskAssessmentResponse[], string>({
            query: (id) => `/investigations/${id}/risk-assessment/history`,
            transformResponse: (response: any) => {
                // baseQuery unwraps outer { success, data } → inner may still be { success, data: [...] }
                if (response?.data && response?.success !== undefined) {
                    return response.data;
                }
                return Array.isArray(response) ? response : [];
            },
            providesTags: (_result, _error, id) => [{ type: 'RiskAssessment', id }],
        }),

        uploadAgentDocument: builder.mutation<InvestigationDocument, { id: string; file: File; documentType: string }>({
            query: ({ id, file, documentType }) => {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('documentType', documentType);
                return {
                    url: `/investigations/${id}/agent-documents/upload`,
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Investigation', id }],
        }),

        uploadMultipleAgentDocuments: builder.mutation<InvestigationDocument[], { id: string; files: File[]; documentTypes: string[] }>({
            query: ({ id, files, documentTypes }) => {
                const formData = new FormData();
                files.forEach((file) => formData.append('files', file));
                formData.append('documentTypes', documentTypes.join(','));
                return {
                    url: `/investigations/${id}/agent-documents/upload-multiple`,
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Investigation', id }],
        }),

        approveDocument: builder.mutation<InvestigationDocument, { investigationId: string; documentId: string; data: DocumentApprovalRequest }>({
            query: ({ investigationId, documentId, data }) => ({
                url: `/investigations/${investigationId}/documents/${documentId}/approval`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_result, _error, { investigationId }) => [{ type: 'Investigation', id: investigationId }],
        }),

        getFilteredDocuments: builder.query<InvestigationDocument[], { id: string; uploadedBy?: string }>({
            query: ({ id, uploadedBy }) => ({
                url: `/investigations/${id}/documents`,
                params: uploadedBy ? { uploadedBy } : {},
            }),
            providesTags: (_result, _error, { id }) => [{ type: 'Investigation', id }],
        }),

        // ==================== PAYMENTS ====================
        getCheckOptions: builder.query<CheckOption[], void>({
            query: () => '/payments/check-options',
            providesTags: ['CheckOptions'],
        }),

        initiateFlutterwavePayment: builder.mutation<InitiateFlutterwavePaymentResponse, InitiateFlutterwavePaymentRequest>({
            query: (data) => ({
                url: '/payments/flutterwave/initiate',
                method: 'POST',
                body: data,
            }),
        }),

        verifyFlutterwavePayment: builder.mutation<VerifyFlutterwavePaymentResponse, VerifyFlutterwavePaymentRequest>({
            query: ({ tx_ref }) => ({
                url: '/payments/flutterwave/verify',
                method: 'GET',
                params: { tx_ref },
            }),
            invalidatesTags: ['Investigation', 'Transaction'],
        }),

        getMyTransactions: builder.query<Transaction[], void>({
            query: () => '/payments/transactions',
            providesTags: ['Transaction'],
        }),

        getTransactionById: builder.query<Transaction, string>({
            query: (id) => `/payments/transactions/${id}`,
            providesTags: (_result, _error, id) => [{ type: 'Transaction', id }],
        }),

        getReusablePayments: builder.query<ReusablePayment[], void>({
            query: () => '/payments/reusable-payments',
            providesTags: ['Transaction'],
        }),

        // ==================== EARNINGS (AGENT) ====================
        getMyEarnings: builder.query<Earning[], EarningStatus | undefined>({
            query: (status) => ({
                url: '/earnings/my-earnings',
                params: status ? { status } : {},
            }),
            providesTags: ['Earning'],
        }),

        getMyEarningsSummary: builder.query<EarningsSummary, void>({
            query: () => '/earnings/my-summary',
            providesTags: ['Earning'],
        }),

        getEarningsBreakdown: builder.query<EarningsBreakdown, void>({
            query: () => '/analytics/earnings/breakdown',
            providesTags: ['Earning', 'Analytics'],
        }),

        // ==================== ANALYTICS ====================
        getDashboardStats: builder.query<DashboardStats, { startDate?: string; endDate?: string } | void>({
            query: (params) => ({
                url: '/analytics/dashboard',
                params: params || {},
            }),
            providesTags: ['Analytics'],
        }),

        getInvestigationTrends: builder.query<InvestigationTrend[], { startDate?: string }>({
            query: (params) => ({
                url: '/analytics/investigations/trends',
                params,
            }),
            providesTags: ['Analytics'],
        }),

        getRevenueTrends: builder.query<RevenueTrend[], { startDate?: string }>({
            query: (params) => ({
                url: '/analytics/revenue/trends',
                params,
            }),
            providesTags: ['Analytics'],
        }),

        getTopAgents: builder.query<TopAgent[], { limit?: number }>({
            query: (params) => ({
                url: '/analytics/agents/top',
                params,
            }),
            providesTags: ['Analytics', 'Admin'],
        }),

        getStatusDistribution: builder.query<StatusDistribution[], void>({
            query: () => '/analytics/investigations/status-distribution',
            providesTags: ['Analytics'],
        }),

        getFinancialStats: builder.query<FinancialStats, { startDate?: string; endDate?: string }>({
            query: (params) => ({
                url: '/analytics/financial/stats',
                params,
            }),
            providesTags: ['Analytics'],
        }),

        // ==================== ADMIN (SUPER_ADMIN) ====================
        inviteAgent: builder.mutation<User, InviteAgentRequest>({
            query: (data) => ({
                url: '/admin/agents/invite',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Admin', 'User'],
        }),

        getAllUsers: builder.query<User[], { role?: string; status?: string }>({
            query: (params) => ({
                url: '/admin/users',
                params,
            }),
            providesTags: ['Admin', 'User'],
        }),

        getUserById: builder.query<UserWithStats, string>({
            query: (id) => `/admin/users/${id}`,
            providesTags: (_result, _error, id) => [{ type: 'User', id }],
        }),

        updateUserStatus: builder.mutation<User, { id: string; data: UpdateUserStatusRequest }>({
            query: ({ id, data }) => ({
                url: `/admin/users/${id}/status`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'User', id }, 'Admin', 'User'],
        }),

        deleteUser: builder.mutation<{ message: string }, string>({
            query: (id) => ({
                url: `/admin/users/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Admin', 'User'],
        }),

        getAllInvestigations: builder.query<
            Investigation[],
            { status?: string; agentId?: string; employerId?: string }
        >({
            query: (params) => ({
                url: '/admin/investigations',
                params,
            }),
            providesTags: ['Admin', 'Investigation'],
        }),

        reassignInvestigation: builder.mutation<Investigation, { id: string; data: ReassignInvestigationRequest }>({
            query: ({ id, data }) => ({
                url: `/admin/investigations/${id}/reassign`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [
                { type: 'Investigation', id },
                'Admin',
                'Investigation',
            ],
        }),

        getPlatformStatistics: builder.query<PlatformStatistics, void>({
            query: () => '/admin/statistics',
            providesTags: ['Admin', 'Analytics'],
        }),

        getAllEarnings: builder.query<Earning[], { status?: EarningStatus }>({
            query: (params) => ({
                url: '/earnings',
                params,
            }),
            providesTags: ['Admin', 'Earning'],
        }),

        getPlatformEarningsStats: builder.query<PlatformEarningsStats, void>({
            query: () => '/earnings/platform-stats',
            providesTags: ['Admin', 'Earning', 'Analytics'],
        }),

        markEarningAsPaid: builder.mutation<MarkEarningPaidResponse, string>({
            query: (id) => ({
                url: `/earnings/${id}/mark-paid`,
                method: 'PATCH',
            }),
            invalidatesTags: ['Earning', 'Admin'],
        }),

        bulkMarkEarningsAsPaid: builder.mutation<BulkMarkPaidResponse, BulkMarkPaidRequest>({
            query: (data) => ({
                url: '/earnings/bulk/mark-paid',
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: ['Earning', 'Admin'],
        }),

        // ==================== WAITLIST ====================
        joinWaitlist: builder.mutation<WaitlistResponse, WaitlistEntry>({
            query: (data) => ({
                url: '/waitlist',
                method: 'POST',
                body: data,
            }),
        }),

        getAllWaitlistEntries: builder.query<WaitlistResponse[], { status?: string }>({
            query: (params) => ({
                url: '/waitlist',
                params,
            }),
            providesTags: ['Waitlist'],
        }),

        getWaitlistStats: builder.query<WaitlistStats, void>({
            query: () => '/waitlist/stats',
            providesTags: ['Waitlist'],
        }),

        checkEmailOnWaitlist: builder.query<CheckEmailResponse, string>({
            query: (email) => `/waitlist/check/${email}`,
        }),

        getWaitlistEntryById: builder.query<WaitlistResponse, number>({
            query: (id) => `/waitlist/${id}`,
            providesTags: (_result, _error, id) => [{ type: 'Waitlist', id }],
        }),

        updateWaitlistEntry: builder.mutation<WaitlistResponse, { id: number; data: UpdateWaitlistRequest }>({
            query: ({ id, data }) => ({
                url: `/waitlist/${id}`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Waitlist', id }, 'Waitlist'],
        }),

        bulkUpdateWaitlistStatus: builder.mutation<WaitlistResponse[], BulkUpdateWaitlistRequest>({
            query: (data) => ({
                url: '/waitlist/bulk/status',
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: ['Waitlist'],
        }),

        deleteWaitlistEntry: builder.mutation<void, number>({
            query: (id) => ({
                url: `/waitlist/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Waitlist'],
        }),

        // ==================== SUPPORT TICKETS ====================
        // User endpoints
        createSupportTicket: builder.mutation<SupportTicket, CreateTicketRequest>({
            query: (data) => ({
                url: '/support/tickets',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['SupportTicket'],
        }),

        getMyTickets: builder.query<SupportTicket[], void>({
            query: () => '/support/tickets/my-tickets',
            providesTags: ['SupportTicket'],
        }),

        getMyTicketById: builder.query<SupportTicket, string>({
            query: (id) => `/support/tickets/my-tickets/${id}`,
            providesTags: (_result, _error, id) => [{ type: 'SupportTicket', id }],
        }),

        // Admin endpoints
        getAllTickets: builder.query<SupportTicket[], { status?: TicketStatus; priority?: TicketPriority; category?: TicketCategory }>({
            query: (params) => ({
                url: '/support/admin/tickets',
                params,
            }),
            providesTags: ['SupportTicket'],
        }),

        getTicketById: builder.query<SupportTicket, string>({
            query: (id) => `/support/admin/tickets/${id}`,
            providesTags: (_result, _error, id) => [{ type: 'SupportTicket', id }],
        }),

        updateTicketStatus: builder.mutation<SupportTicket, { id: string; data: UpdateTicketStatusRequest }>({
            query: ({ id, data }) => ({
                url: `/support/admin/tickets/${id}/status`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'SupportTicket', id }, 'SupportTicket'],
        }),

        updateTicketPriority: builder.mutation<SupportTicket, { id: string; data: UpdateTicketPriorityRequest }>({
            query: ({ id, data }) => ({
                url: `/support/admin/tickets/${id}/priority`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'SupportTicket', id }, 'SupportTicket'],
        }),

        assignTicket: builder.mutation<SupportTicket, { id: string; data: AssignTicketRequest }>({
            query: ({ id, data }) => ({
                url: `/support/admin/tickets/${id}/assign`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'SupportTicket', id }, 'SupportTicket'],
        }),

        getTicketStatistics: builder.query<TicketStatistics, void>({
            query: () => '/support/admin/statistics',
            providesTags: ['SupportTicket'],
        }),

        // ==================== BANK ACCOUNTS ====================
        // User endpoints
        addBankAccount: builder.mutation<BankAccount, AddBankAccountRequest>({
            query: (data) => ({
                url: '/bank-accounts',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['BankAccount'],
        }),

        getMyBankAccounts: builder.query<BankAccount[], void>({
            query: () => '/bank-accounts/my-accounts',
            providesTags: ['BankAccount'],
        }),

        getMyBankAccountById: builder.query<BankAccount, string>({
            query: (id) => `/bank-accounts/my-accounts/${id}`,
            providesTags: (_result, _error, id) => [{ type: 'BankAccount', id }],
        }),

        updateBankAccount: builder.mutation<BankAccount, { id: string; data: UpdateBankAccountRequest }>({
            query: ({ id, data }) => ({
                url: `/bank-accounts/${id}`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'BankAccount', id }, 'BankAccount'],
        }),

        setDefaultBankAccount: builder.mutation<BankAccount, string>({
            query: (id) => ({
                url: `/bank-accounts/${id}/set-default`,
                method: 'PATCH',
            }),
            invalidatesTags: ['BankAccount'],
        }),

        deleteBankAccount: builder.mutation<{ message: string }, string>({
            query: (id) => ({
                url: `/bank-accounts/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['BankAccount'],
        }),

        // Admin endpoints
        getAllBankAccounts: builder.query<BankAccount[], void>({
            query: () => '/bank-accounts/admin/all',
            providesTags: ['BankAccount'],
        }),

        // ==================== WITHDRAWALS ====================
        // User endpoints
        createWithdrawal: builder.mutation<Withdrawal, CreateWithdrawalRequest>({
            query: (data) => ({
                url: '/withdrawals',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Withdrawal'],
        }),

        getMyWithdrawals: builder.query<Withdrawal[], void>({
            query: () => '/withdrawals/my-withdrawals',
            providesTags: ['Withdrawal'],
        }),

        getMyWithdrawalById: builder.query<Withdrawal, string>({
            query: (id) => `/withdrawals/my-withdrawals/${id}`,
            providesTags: (_result, _error, id) => [{ type: 'Withdrawal', id }],
        }),

        // Admin endpoints
        getAllWithdrawals: builder.query<Withdrawal[], { status?: WithdrawalStatus }>({
            query: (params) => ({
                url: '/withdrawals/admin/all',
                params,
            }),
            providesTags: ['Withdrawal'],
        }),

        getWithdrawalById: builder.query<Withdrawal, string>({
            query: (id) => `/withdrawals/admin/${id}`,
            providesTags: (_result, _error, id) => [{ type: 'Withdrawal', id }],
        }),

        reviewWithdrawal: builder.mutation<Withdrawal, { id: string; data: ReviewWithdrawalRequest }>({
            query: ({ id, data }) => ({
                url: `/withdrawals/admin/${id}/review`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Withdrawal', id }, 'Withdrawal'],
        }),

        processWithdrawal: builder.mutation<Withdrawal, { id: string; data: ProcessWithdrawalRequest }>({
            query: ({ id, data }) => ({
                url: `/withdrawals/admin/${id}/process`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Withdrawal', id }, 'Withdrawal'],
        }),

        getWithdrawalStatistics: builder.query<WithdrawalStatistics, void>({
            query: () => '/withdrawals/admin/statistics/summary',
            providesTags: ['Withdrawal'],
        }),
    }),
})

// Export hooks for usage in functional components
export const {
    // Auth
    useLoginMutation,
    useLoginVerifyMutation,
    useRegisterMutation,
    useGetProfileQuery,
    useLazyGetProfileQuery,
    useSendOtpMutation,
    useVerifyOtpMutation,
    useSendEmailVerificationMutation,
    useVerifyEmailMutation,
    // Investigations
    useValidateInvestigationMutation,
    useCreateInvestigationMutation,
    useGetMyInvestigationsQuery,
    useGetInvestigationByIdQuery,
    useGetInvestigationByLinkQuery,
    useSubmitEmployeeFormMutation,
    useGetInvestigationStatsQuery,
    useAssignAgentMutation,
    useStartInvestigationMutation,
    useSubmitReportMutation,
    useValidateInvestigationReportMutation,
    useUploadInvestigationDocumentMutation,
    useGetInvestigationDocumentsQuery,
    useValidateRequiredDocumentsQuery,
    useDeleteDocumentMutation,
    useGetScoringCriteriaQuery,
    useSubmitRiskAssessmentMutation,
    useGetRiskAssessmentQuery,
    useGetRiskAssessmentHistoryQuery,
    useUploadAgentDocumentMutation,
    useUploadMultipleAgentDocumentsMutation,
    useApproveDocumentMutation,
    useGetFilteredDocumentsQuery,
    // Payments
    useGetCheckOptionsQuery,
    useInitiateFlutterwavePaymentMutation,
    useVerifyFlutterwavePaymentMutation,
    useGetMyTransactionsQuery,
    useGetTransactionByIdQuery,
    useGetReusablePaymentsQuery,
    // Earnings
    useGetMyEarningsQuery,
    useGetMyEarningsSummaryQuery,
    useGetEarningsBreakdownQuery,
    // Analytics
    useGetDashboardStatsQuery,
    useGetInvestigationTrendsQuery,
    useGetRevenueTrendsQuery,
    useGetTopAgentsQuery,
    useGetStatusDistributionQuery,
    useGetFinancialStatsQuery,
    // Admin
    useInviteAgentMutation,
    useGetAllUsersQuery,
    useGetUserByIdQuery,
    useUpdateUserStatusMutation,
    useDeleteUserMutation,
    useGetAllInvestigationsQuery,
    useReassignInvestigationMutation,
    useGetPlatformStatisticsQuery,
    useGetAllEarningsQuery,
    useGetPlatformEarningsStatsQuery,
    useMarkEarningAsPaidMutation,
    useBulkMarkEarningsAsPaidMutation,
    // Waitlist
    useJoinWaitlistMutation,
    useGetAllWaitlistEntriesQuery,
    useGetWaitlistStatsQuery,
    useCheckEmailOnWaitlistQuery,
    useGetWaitlistEntryByIdQuery,
    useUpdateWaitlistEntryMutation,
    useBulkUpdateWaitlistStatusMutation,
    useDeleteWaitlistEntryMutation,
    // Support Tickets
    useCreateSupportTicketMutation,
    useGetMyTicketsQuery,
    useGetMyTicketByIdQuery,
    useGetAllTicketsQuery,
    useGetTicketByIdQuery,
    useUpdateTicketStatusMutation,
    useUpdateTicketPriorityMutation,
    useAssignTicketMutation,
    useGetTicketStatisticsQuery,
    // Bank Accounts
    useAddBankAccountMutation,
    useGetMyBankAccountsQuery,
    useGetMyBankAccountByIdQuery,
    useUpdateBankAccountMutation,
    useSetDefaultBankAccountMutation,
    useDeleteBankAccountMutation,
    useGetAllBankAccountsQuery,
    // Withdrawals
    useCreateWithdrawalMutation,
    useGetMyWithdrawalsQuery,
    useGetMyWithdrawalByIdQuery,
    useGetAllWithdrawalsQuery,
    useGetWithdrawalByIdQuery,
    useReviewWithdrawalMutation,
    useProcessWithdrawalMutation,
    useGetWithdrawalStatisticsQuery,
} = api
